import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Input } from "@/components/ui/input";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useIngredientCategoriesQuery } from "../api/ingredient-categories-queries";

export function IngredientsFilter() {
  const nav = useNavigate({ from: "/app/inventory/ingredients" });
  const search = useSearch({
    from: "/app/inventory/ingredients",
  }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const nameFilter = findCondition(filters, "name");
  const isActiveFilter = findCondition(filters, "isActive");
  const categoryFilter = findCondition(filters, "categoryId");
  const categoriesQuery = useIngredientCategoriesQuery({});
  const categoryOptions = useMemo(() => {
    const rows = categoriesQuery.data ?? [];
    return [
      { value: "all", label: "ທຸກຫມວດ" },
      { value: "__uncategorized__", label: "ຍັງບໍ່ຈັດຫມວດ" },
      ...rows.map((c) => ({
        value: c.id,
        label: c.name,
      })),
    ];
  }, [categoriesQuery.data]);

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || "",
  );

  useEffect(() => {
    const nameVal = nameFilter?.value as string;
    setSearchValue(nameVal || "");
  }, [nameFilter?.value]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "name",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "name");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const isActiveValue =
    typeof isActiveFilter?.value === "boolean"
      ? String(isActiveFilter.value)
      : "";
  const categoryValue = (categoryFilter?.value as string) || "all";
  const hasFilter =
    !!nameFilter?.value || isActiveValue !== "" || categoryValue !== "all";

  const clearFilters = () => {
    setSearchValue("");
    nav({
      search: {
        ...search,
        offset: 0,
        filters: undefined,
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 pb-2">
      <Input
        placeholder="ຄົ້ນຫາຕາມຊື່ວັດຖຸດິບ..."
        defaultValue={searchValue}
        onChange={(e) => debounced(e.target.value)}
        className="max-w-xs"
      />
      <SimpleSelect
        placeholder="ຫມວດ"
        value={categoryValue}
        onValueChange={(v) => {
          const next =
            v === "all"
              ? removeConditions(filters, "categoryId")
              : upsertCondition(filters, {
                  field: "categoryId",
                  op: "eq",
                  value: v,
                });
          nav({
            search: {
              ...search,
              offset: 0,
              filters: next?.length ? next : undefined,
            },
          });
        }}
        options={categoryOptions}
      />
      <SimpleSelect
        placeholder="ສະຖານະ"
        value={isActiveValue || "all"}
        onValueChange={(v) => {
          const next =
            v === "all"
              ? removeConditions(filters, "isActive")
              : upsertCondition(filters, {
                  field: "isActive",
                  op: "eq",
                  value: v === "true",
                });
          nav({
            search: {
              ...search,
              offset: 0,
              filters: next?.length ? next : undefined,
            },
          });
        }}
        options={[
          { value: "all", label: "ທຸກສະຖານະ" },
          { value: "true", label: "ເປີດ" },
          { value: "false", label: "ປິດ" },
        ]}
      />
      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="h-4 w-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
